import React, { ReactNode } from 'react';

import { RadRestriction, RadRestrictions, RadRestrictionsRequest } from '../../../model/SOL49';
import { FlightDto, LatLonPoint, TimePeriod } from '../../../model/CORE';
import ExtensiblePanel, { ExtensiblePanelProps, ExtensiblePanelState } from '../../../common/panel/ExtensiblePanel';
import memoizeOne from 'memoize-one';
import { RadListPanel } from './panel/RadListPanel';
import './ApplicableRadRestrictionPanel.less';
import RadMapPanel from './panel/RadMapPanel';
import { ServiceFactory } from '../../../service/ServiceFactory';
import { notification } from 'antd';
import { RadInformationPanel } from './panel/RadInformationPanel';

interface ApplicableRadPanelProps extends ExtensiblePanelProps {
    selectedAreaPolygon?: Array<LatLonPoint>;
    initialTimePeriod: TimePeriod;
    initialRadSelectionId?: string;
    applicableRadRestrictions?: RadRestrictions;
}

interface ApplicableRadPanelState extends ExtensiblePanelState {
    selectedTimePeriod: TimePeriod;
    selectedRadRestriction: RadRestriction;
    applicableRadRestrictions: RadRestrictions;
    selectedFlights?: Array<FlightDto>;
}

export class ApplicableRadRestrictionPanel extends ExtensiblePanel<ApplicableRadPanelProps, ApplicableRadPanelState> {
    public static LIST_PANEL_WIDTH_EM: number = 40;
    public static INFO_PANEL_WIDTH_EM: number = 90;
    public static MAP_PANEL_WIDTH_EM: number = 80;
    public static INITIAL_WIDTH_FULL: string =
        this.LIST_PANEL_WIDTH_EM + this.INFO_PANEL_WIDTH_EM + this.MAP_PANEL_WIDTH_EM + 'em';
    public static INITIAL_WIDTH_LIST: string = this.LIST_PANEL_WIDTH_EM + 'em';

    constructor(props: ApplicableRadPanelProps) {
        super(props);
        let selectedRestriction: RadRestriction;
        if (props.applicableRadRestrictions && props.initialRadSelectionId) {
            selectedRestriction = [
                ...props.applicableRadRestrictions.offLoadingRadRestrictions,
                ...props.applicableRadRestrictions.onLoadingRadRestrictions,
                ...props.applicableRadRestrictions.offLoadingAndOnloadingRadRestrictions,
            ].find(restriction => restriction.id === props.initialRadSelectionId);
        }
        this.state = Object.assign(this.state, {
            selectedRadRestriction: selectedRestriction,
            selectedTimePeriod: props.initialTimePeriod,
            showSeparator: false,
            panelIdxVisible: selectedRestriction ? (selectedRestriction.preAgreed ? 2 : 1) : 0,
            applicableRadRestrictions: props.applicableRadRestrictions,
        });
    }

    componentDidMount() {
        this.getRadRestrictions();
    }

    private getRadRestrictions() {
        if (this.props.selectedAreaPolygon) {
            this.setState({ loading: true });
            const req: RadRestrictionsRequest = {
                areaPolygon: this.props.selectedAreaPolygon,
            };
            ServiceFactory.sol49Service()
                .getRadRestrictionsByPolygon(req)
                .then(reply => {
                    if (reply.error) {
                        throw new Error(reply.error.message);
                    }
                    this.setState({
                        applicableRadRestrictions: reply.data,
                        loading: false,
                    });
                })
                .catch(reason => {
                    notification.error({ message: 'Error', description: reason.message, duration: 0 });
                });
        }
    }

    getPanels(): Map<React.ReactNode, number> {
        const panels = new Map<ReactNode, number>();
        if (this.state.selectedRadRestriction && !this.state.selectedRadRestriction.preAgreed) {
            panels.set(this.getListPanel(this.state), ApplicableRadRestrictionPanel.LIST_PANEL_WIDTH_EM);
            panels.set(this.getMapPanel(this.state), ApplicableRadRestrictionPanel.MAP_PANEL_WIDTH_EM);
        } else {
            panels.set(this.getListPanel(this.state), ApplicableRadRestrictionPanel.LIST_PANEL_WIDTH_EM);
            panels.set(this.getInformationPanel(this.state), ApplicableRadRestrictionPanel.INFO_PANEL_WIDTH_EM);
            panels.set(this.getMapPanel(this.state), ApplicableRadRestrictionPanel.MAP_PANEL_WIDTH_EM);
        }
        return panels;
    }

    private onSelectFlights = (flights: FlightDto[]) => {
        this.setState({ selectedFlights: flights });
    };

    private getListPanel = memoizeOne(state => {
        return (
            <RadListPanel
                applicableRadRestrictions={this.state.applicableRadRestrictions}
                selectedRadRestriction={state.selectedRadRestriction}
                selectedTimePeriod={state.selectedTimePeriod}
                onSelectRadRestriction={this.onRadRestrictionSelected}
                onSelectedTimePeriodChange={this.onSelectedTimePeriodChange}
            />
        );
    });

    private getInformationPanel = memoizeOne(state => {
        return (
            <RadInformationPanel
                selectedRadRestriction={state.selectedRadRestriction}
                selectedTimePeriod={state.selectedTimePeriod}
                onFLightsSelected={this.onSelectFlights}
            />
        );
    });

    private getMapPanel = memoizeOne(state => {
        return (
            <RadMapPanel
                selectedRadRestriction={this.state.selectedRadRestriction}
                selectedFlights={this.state.selectedFlights}
                selectedTimePeriod={state.selectedTimePeriod}
            />
        );
    });

    /**
     * HANDLERS
     */
    private onRadRestrictionSelected = (radRestriction: RadRestriction) => {
        this.setState(
            {
                selectedRadRestriction: radRestriction,
            },
            () => {
                if (radRestriction.preAgreed) {
                    this.showSidePanel(2);
                } else {
                    this.showSidePanel(1);
                }
            },
        );
    };

    private onSelectedTimePeriodChange = (selectedTimePeriod: TimePeriod) => {
        this.setState({
            selectedTimePeriod: selectedTimePeriod,
        });
    };
}
